import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Chip,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TableCell,
  TableRow,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { memo, useCallback, useState } from "react";
import {
  type SmsConsentAdminFieldsFragment,
  SmsConsentStatus,
} from "../../generated/graphql";
import {
  BASIS_LABELS,
  EVENT_LABELS,
  SMS_CONSENT_COLUMN_COUNT,
  STATUS_LABELS,
  deliveryFailureLabel,
  formatDate,
  isSkippingTexts,
  lineTypeLabel,
} from "./sms-consent-display";

const STATUS_COLORS: Record<SmsConsentStatus, "success" | "warning" | "error"> =
  {
    [SmsConsentStatus.Granted]: "success",
    [SmsConsentStatus.Pending]: "warning",
    [SmsConsentStatus.Revoked]: "error",
  };

const styles = {
  summaryRow: { "& > *": { borderBottom: "unset" } } satisfies SxProps<Theme>,
  historyCell: { py: 0 } satisfies SxProps<Theme>,
  historyList: { pl: 6 } satisfies SxProps<Theme>,
};

type Props = { readonly row: SmsConsentAdminFieldsFragment };

/** One number's consent, with its event history behind the expand toggle. */
const SmsConsentRow = memo(({ row }: Props) => {
  const [open, setOpen] = useState(false);
  const toggle = useCallback(() => {
    setOpen((value) => !value);
  }, []);

  return (
    <>
      <TableRow sx={styles.summaryRow}>
        <TableCell padding="checkbox">
          <IconButton
            aria-label={open ? "Hide history" : "Show history"}
            size="small"
            onClick={toggle}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{row.maskedPhoneNumber}</TableCell>
        <TableCell>
          <Chip
            color={STATUS_COLORS[row.status]}
            label={STATUS_LABELS[row.status]}
            size="small"
          />
        </TableCell>
        <TableCell>{BASIS_LABELS[row.basis]}</TableCell>
        <TableCell>{formatDate(row.createdAt)}</TableCell>
        <TableCell>{formatDate(row.optedInAt)}</TableCell>
        <TableCell>{formatDate(row.revokedAt)}</TableCell>
        <TableCell>{lineTypeLabel(row.landline)}</TableCell>
        <TableCell>{formatDate(row.lineTypeCheckedAt)}</TableCell>
        <TableCell>
          <Stack alignItems="flex-start" spacing={0.5}>
            {isSkippingTexts(row.undeliverableUntil) && (
              <Chip
                color="error"
                label={`Undeliverable, texts skipped until ${formatDate(row.undeliverableUntil)}`}
                size="small"
              />
            )}
            <Typography variant="body2">{deliveryFailureLabel(row)}</Typography>
          </Stack>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={SMS_CONSENT_COLUMN_COUNT} sx={styles.historyCell}>
          <Collapse unmountOnExit in={open} timeout="auto">
            <List dense sx={styles.historyList}>
              {row.historyTruncated && (
                <ListItem>
                  <ListItemText secondary="Older changes are not shown; only the most recent ones are listed." />
                </ListItem>
              )}
              {row.events.length === 0 && (
                <ListItem>
                  <ListItemText primary="No recorded changes." />
                </ListItem>
              )}
              {row.events.map((event) => (
                <ListItem key={event.uuid}>
                  <ListItemText
                    primary={EVENT_LABELS[event.action]}
                    secondary={formatDate(event.occurredAt)}
                  />
                </ListItem>
              ))}
            </List>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
});

SmsConsentRow.displayName = "SmsConsentRow";

export default SmsConsentRow;
